<template id="admin-shipping">
  <section>
    <div class="d-flex justify-content-between align-items-center flex-wrap">
      <h4 class="mb-0">{{ isAmazon ? 'Amazon Shipment (Excel)' : 'Delhivery Create Shipment' }}</h4>
      <b-button variant="secondary" size="sm" to="/admin/orders">
        <font-awesome-icon :icon="['far','arrow-left']"></font-awesome-icon>
        Orders
      </b-button>
    </div>

    <b-alert show variant="warning" v-if="!orderIds.length" class="mt-3">
      No orders selected, select orders in the Orders page and choose the Amazon / Delhivery action.
    </b-alert>

    <div class="d-flex flex-wrap align-items-end mt-3" v-if="shipments.length">
      <template v-if="isAmazon">
        <b-form-group label="Pickup Date" class="mr-3">
          <b-form-input type="date" v-model="date"/>
        </b-form-group>
        <b-form-group label="Pickup Time" class="mr-3">
          <b-form-select v-model="time" :options="['11:00 - 14:00', '14:00 - 16:15']"/>
        </b-form-group>
      </template>
      <b-form-group label="Box size (w x h x l cm) & weight (kg) for all" class="mr-3">
        <b-input-group>
          <b-form-input type="number" v-model.number="all.w" placeholder="w" min="5" max="50"/>
          <b-form-input type="number" v-model.number="all.h" placeholder="h" min="5" max="50"/>
          <b-form-input type="number" v-model.number="all.l" placeholder="l" min="5" max="50"/>
          <b-form-input type="number" v-model.number="all.weight" placeholder="kg" step="any"/>
          <b-input-group-append>
            <b-button variant="outline-info" @click="applyAll">Apply to all</b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
    </div>

    <div class="row">
      <div class="col-sm-12 col-md-6 col-lg-4 col-xl-3 p-2" v-for="s of shipments" :key="s.orderId">
        <b-card :border-variant="cardVariant(s)" class="h-100 bg-transparent">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <strong>#{{ s.orderNum }} <small class="text-muted">{{ s.orderName }}</small></strong>
              <b-button size="sm" variant="link" class="text-danger p-0" title="Remove from this batch"
                        @click="remove(s)" v-if="!done(s)">
                <font-awesome-icon :icon="['far','times']"></font-awesome-icon>
              </b-button>
            </div>
          </template>

          <b-alert show variant="success" class="mb-0" v-if="done(s)">
            Shipped &mdash; tracking <code>{{ s.trackingNumber }}</code>
          </b-alert>
          <template v-else>
            <b-alert show variant="danger" v-if="errors[s.orderId]" class="small">
              <div v-for="e of errors[s.orderId]" :key="e">{{ e }}</div>
            </b-alert>
            <b-form-group label="Name of Person">
              <b-form-input v-model="s.name"/>
            </b-form-group>
            <b-form-group label="Phone Number">
              <b-form-input v-model="s.phone" :state="s.phone.trim().length === 10 ? null : false"/>
            </b-form-group>
            <b-form-group :label="`Address Line 1 ${isAmazon ? `(${s.address1.length}/60)` : ''}`">
              <b-form-textarea v-model="s.address1" rows="2" :state="lenState(s.address1)"/>
            </b-form-group>
            <b-form-group :label="`Address Line 2 ${isAmazon ? `(${s.address2.length}/60)` : ''}`">
              <b-form-textarea v-model="s.address2" rows="2" :state="lenState(s.address2)"/>
            </b-form-group>
            <div class="d-flex">
              <b-form-group label="City" class="mr-2 flex-grow-1">
                <b-form-input v-model="s.city"/>
              </b-form-group>
              <b-form-group label="Pincode" style="max-width: 110px">
                <b-form-input v-model="s.pincode" :state="s.pincode.trim().length === 6 ? null : false"/>
              </b-form-group>
            </div>
            <b-form-group label="State">
              <b-form-select v-model="s.state" :options="states" :state="s.state ? null : false">
                <template #first>
                  <b-form-select-option value="">-- select state --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
            <b-form-group label="Box size (w x h x l cm)">
              <b-input-group>
                <b-form-input type="number" v-model.number="s.w" placeholder="w" min="5" max="50"/>
                <b-form-input type="number" v-model.number="s.h" placeholder="h" min="5" max="50"/>
                <b-form-input type="number" v-model.number="s.l" placeholder="l" min="5" max="50"/>
              </b-input-group>
            </b-form-group>
            <b-form-group label="Weight (kg)" class="mb-0">
              <b-form-input type="number" step="any" v-model.number="s.weight" placeholder="weight"/>
            </b-form-group>
          </template>
        </b-card>
      </div>
    </div>

    <div class="d-flex justify-content-center my-4" v-if="shipments.length">
      <b-button v-if="isAmazon" variant="success" size="lg" :disabled="busy" @click="downloadExcel">
        <font-awesome-icon :icon="['far','file-excel']"></font-awesome-icon>
        Download Excel
      </b-button>
      <template v-else>
        <b-button variant="success" size="lg" class="mr-3" :disabled="busy || !pending.length" @click="createDelhivery">
          <b-spinner small v-if="busy"></b-spinner>
          <font-awesome-icon v-else :icon="['far','truck']"></font-awesome-icon>
          Create {{ pending.length }} Delhivery Shipment(s)
        </b-button>
        <b-button variant="primary" size="lg" :disabled="!shipped.length" @click="printLabels">
          <font-awesome-icon :icon="['far','print']"></font-awesome-icon>
          Print {{ shipped.length }} Label(s)
        </b-button>
      </template>
    </div>

    <b-card v-if="isAmazon && orderIds.length" class="bg-transparent mb-5" title="Upload Amazon result file">
      <p class="text-muted small">
        After uploading the excel on Amazon, download the processed file and upload it here, the orders get
        marked as shipped and customers are informed with the tracking link.
      </p>
      <b-input-group>
        <b-form-file v-model="resultFile" accept=".xlsx" placeholder="Choose processed file"/>
        <b-input-group-append>
          <b-button variant="success" :disabled="!resultFile || busy" @click="importAmazon">Upload</b-button>
        </b-input-group-append>
      </b-input-group>
      <b-table v-if="importResults" :items="importResults" :fields="importFields" small class="mt-3" show-empty
               empty-text="No rows found in the file">
        <template #cell(success)="{ item }">
          <b-badge :variant="item.success ? 'success' : 'danger'">{{ item.success ? 'shipped' : 'skipped' }}</b-badge>
        </template>
      </b-table>
    </b-card>
  </section>
</template>
<script>
import axios from 'axios'
import fileDownload from 'js-file-download'
import {format} from 'date-fns'

const DIMS_KEY = 'shipping-box-dims'
// a literal closing script tag would end this SFC block
const SCRIPT_END = '</' + 'script>'

const esc = v => String(v == null ? '' : v).replace(/[&<>"']/g, c => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
})[c])

export default {
  name: 'AdminShipping',
  data: () => ({
    states: [],
    shipments: [],
    errors: {},
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '11:00 - 14:00',
    all: {w: null, h: null, l: null, weight: null},
    busy: false,
    resultFile: null,
    importResults: null,
    importFields: [
      {key: 'orderNum', label: 'Order'},
      {key: 'trackingNumber', label: 'Tracking'},
      {key: 'success', label: 'Status'},
      {key: 'error', label: 'Remarks'},
    ]
  }),
  computed: {
    service() {
      return this.$route.params.service
    },
    isAmazon() {
      return this.service === 'amazon'
    },
    orderIds() {
      return (this.$route.query.ids || '').split(',').filter(Boolean)
    },
    pending() {
      return this.shipments.filter(s => !this.done(s))
    },
    shipped() {
      return this.shipments.filter(s => this.done(s))
    }
  },
  watch: {
    '$route': 'load'
  },
  created() {
    try {
      Object.assign(this.all, JSON.parse(localStorage.getItem(DIMS_KEY)) || {})
    } catch (e) {
      // ignore
    }
    this.load()
  },
  methods: {
    async load() {
      this.errors = {}
      this.importResults = null
      if (!this.orderIds.length) {
        this.shipments = []
        return
      }
      const {data} = await axios.post('/api/admin/shipping/prefill', {orderIds: this.orderIds})
      this.states = data.states
      this.shipments = data.shipments.map(s => ({
        ...s,
        w: this.all.w, h: this.all.h, l: this.all.l, weight: this.all.weight
      }))
    },
    done(s) {
      return !this.isAmazon && s.modeOfShipment === 'DELHIVERY' && !!s.trackingNumber
    },
    remove(s) {
      this.shipments = this.shipments.filter(x => x !== s)
    },
    applyAll() {
      this.pending.forEach(s => Object.assign(s, this.all))
      try {
        localStorage.setItem(DIMS_KEY, JSON.stringify(this.all))
      } catch (e) {
        // ignore
      }
    },
    lenState(v) {
      return this.isAmazon && v.length > 60 ? false : null
    },
    cardVariant(s) {
      if (this.errors[s.orderId]) return 'danger'
      if (this.done(s)) return 'success'
      return null
    },
    // mirrors the api validation, so the user sees issues before hitting the server
    validate(list) {
      const errors = {}
      list.forEach(s => {
        const e = []
        if (!s.name.trim()) e.push('name is required')
        if (s.phone.trim().length !== 10) e.push('phone must be 10 digits')
        if (s.pincode.trim().length !== 6) e.push('pincode must be 6 digits')
        if (!s.address1.trim()) e.push('address line 1 is required')
        if (this.isAmazon && s.address1.length > 60) e.push('address line 1 is more than 60 characters')
        if (this.isAmazon && s.address2.length > 60) e.push('address line 2 is more than 60 characters')
        if (!s.city.trim()) e.push('city is required')
        if (!s.state) e.push('state is required')
        if (!s.w || !s.h || !s.l) e.push('box size (w x h x l) is required')
        if (!(s.weight > 0)) e.push('weight is required')
        if (e.length) errors[s.orderId] = e
      })
      this.errors = errors
      if (Object.keys(errors).length) {
        this.$bvToast.toast('Please fix the highlighted orders', {title: 'Oops!', variant: 'danger', solid: true})
        return false
      }
      return true
    },
    serverErrors(e) {
      const d = e.response && e.response.data
      if (d && d.errors) this.errors = d.errors
    },
    async downloadExcel() {
      if (!this.validate(this.shipments)) return
      this.busy = true
      try {
        const res = await axios.post('/api/admin/shipping/amazon/excel', {
          date: this.date,
          time: this.time,
          shipments: this.shipments
        }, {responseType: 'blob'})
        fileDownload(res.data, `AmazonUpload-${format(new Date(), 'dd-MM-yyyy-hh-mm-a')}.xlsx`)
      } catch (e) {
        const d = e.response && e.response.data
        if (d instanceof Blob) {
          try {
            this.serverErrors({response: {data: JSON.parse(await d.text())}})
          } catch (ignore) {
            // not json
          }
        }
      } finally {
        this.busy = false
      }
    },
    async importAmazon() {
      const fd = new FormData()
      fd.append('file', this.resultFile)
      this.busy = true
      try {
        const {data} = await axios.post('/api/admin/shipping/amazon/import', fd)
        this.importResults = data
        const ok = data.filter(r => r.success).length
        this.$bvToast.toast(`${ok} order(s) marked as shipped`, {title: 'Amazon', variant: 'success', solid: true})
        this.resultFile = null
      } finally {
        this.busy = false
      }
    },
    async createDelhivery() {
      const list = this.pending
      if (!this.validate(list)) return
      this.busy = true
      try {
        const {data} = await axios.post('/api/admin/shipping/delhivery', {shipments: list})
        const errors = {}
        data.forEach(r => {
          const s = this.shipments.find(x => x.orderId === r.orderId)
          if (!s) return
          if (r.success) {
            s.trackingNumber = r.trackingNumber
            s.modeOfShipment = 'DELHIVERY'
          } else {
            errors[r.orderId] = [r.error || 'failed to create shipment']
          }
        })
        this.errors = errors
        const ok = data.filter(r => r.success).length
        this.$bvToast.toast(`${ok} of ${data.length} shipment(s) created`, {
          title: 'Delhivery',
          variant: ok === data.length ? 'success' : 'warning',
          solid: true
        })
      } catch (e) {
        this.serverErrors(e)
      } finally {
        this.busy = false
      }
    },
    printLabels() {
      const labels = this.shipped.map(s => `
        <div class="label">
          <div class="head">
            <img src="https://api.themilletstore.in/logo.png" alt="">
            <p>Shipped Via Delhivery</p>
          </div>
          <svg class="barcode" jsbarcode-format="CODE128" jsbarcode-value="${esc(s.trackingNumber)}"
               jsbarcode-textmargin="5" jsbarcode-fontoptions="bold"></svg>
          <p class="blk">To:<br>${esc(s.name)}<br>${esc([s.address1, s.address2, s.city, s.state, s.pincode].filter(Boolean).join(', '))}<br>${esc(s.phone)}</p>
          <p class="blk">From:<br>The Millet Store, Bangalore</p>
          <p class="blk">${esc(s.orderName)}, One Box of Grocery Items of Value - Rs ${esc(s.netValue)}/-, Only for Personal Use, not for Re-sale</p>
        </div>`).join('')

      const w = window.open('', '_blank')
      if (!w) {
        this.$bvToast.toast('Allow popups to print labels', {title: 'Oops!', variant: 'danger', solid: true})
        return
      }
      w.document.write(`<!doctype html><html><head><title>Delhivery Labels</title>
        <style>
          @page { size: 105mm 149mm; margin: 0; }
          body { margin: 0; font-family: sans-serif; }
          .label { width: 105mm; height: 149mm; box-sizing: border-box; padding: .75em; display: flex;
                   flex-direction: column; align-items: center; page-break-after: always; }
          .head { display: flex; justify-content: space-between; align-items: center; width: 100%;
                  border-bottom: 1px solid #000; padding-bottom: 5px; margin-bottom: 5px; }
          .head img { width: 64px; }
          .barcode { max-width: 100%; }
          .blk { font-size: .6rem; align-self: flex-start; border-bottom: 1px solid #000; margin: 5px 0 0; }
        </style>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.0/dist/JsBarcode.all.min.js">${SCRIPT_END}
        </head><body>${labels}
        <script>
          window.onload = function () { JsBarcode('.barcode').init(); setTimeout(function () { window.print() }, 300) }
        ${SCRIPT_END}
        </body></html>`)
      w.document.close()
    }
  }
}
</script>
